import React, { useState } from 'react';
import { FaUser, FaEnvelope, FaPhone, FaCalendar, FaClock } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import BaseModal from './BaseModal';

const ScheduleModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    service: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const services = [
    'schedule.service1',
    'schedule.service2',
    'schedule.service3',
    'schedule.service4',
    'schedule.service5',
    'schedule.service6'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      await axios.post(`${apiUrl}/api/contact`, formData);

      setSuccess(true);
      setTimeout(() => {
        onClose();
        setFormData({
          name: '',
          email: '',
          phone: '',
          date: '',
          time: '',
          service: '',
          message: '',
        });
      }, 2000);
    } catch (err) {
      setError(t('contact.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <BaseModal 
      isOpen={isOpen} 
      onClose={onClose}
      maxWidth="max-w-2xl"
    >
      <div className="pt-8">
        <div className="mb-4 sm:mb-6 pr-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">{t('schedule.title')}</h2>
          <p className="text-white/60 text-sm sm:text-base">{t('schedule.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="relative">
              <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors text-sm sm:text-base"
                placeholder={t('schedule.name')}
              />
            </div>

            <div className="relative">
              <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors text-sm sm:text-base"
                placeholder={t('schedule.email')}
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="relative">
              <FaPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors text-sm sm:text-base"
                placeholder={t('schedule.phone')}
              />
            </div>

            <div>
              <select
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#00d4ff] transition-colors text-sm sm:text-base"
              >
                <option value="" className="bg-[#0a0e27]">{t('schedule.selectService')}</option>
                {services.map((serviceKey) => (
                  <option key={serviceKey} value={t(serviceKey)} className="bg-[#0a0e27]">
                    {t(serviceKey)}
                  </option>
                ))}
                <option value={t('schedule.service7')} className="bg-[#0a0e27]">{t('schedule.service7')}</option>
              </select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="relative">
              <FaCalendar className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#00d4ff] transition-colors text-sm sm:text-base"
              />
            </div>

            <div className="relative">
              <FaClock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="time"
                name="time"
                value={formData.time}
                onChange={handleChange}
                required
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#00d4ff] transition-colors text-sm sm:text-base"
              />
            </div>
          </div>

          <div>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors resize-none text-sm sm:text-base"
              placeholder={t('schedule.message')}
            />
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 text-red-400 text-center text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-500/10 border border-green-500/50 rounded-xl p-4 text-green-400 text-center text-sm">
              {t('schedule.successMessage')}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary !py-3 sm:!py-4 text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t('schedule.sending') : t('schedule.submitButton')}
          </button>
        </form>
      </div>
    </BaseModal>
  );
};

export default ScheduleModal;
